import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Logo from '@app/assets/vertical-logo.svg?react';
import GradientHeader from '@app/assets/images/gradient-header.png';
import Balloon from '@app/assets/illustrations/balloon.svg?react';
import ResolvedIcon from '@app/assets/icons/resolved.svg?react';
import { getUserWithBusinesses } from '@app/services/user';
import { UserInterface } from '@app/models/user';

export const TrialEndingLastDay = () => {
  const [user, setUser] = useState<UserInterface | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUserWithBusinesses();
        setUser(response.data.user);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Get first name from user data
  const firstName = user?.firstName || 'User';

  // Show loading state while fetching user data
  if (isLoading) {
    return (
      <div className="flex items-center justify-center flex-col min-h-screen py-6 bg-gray-50">
        <Logo width={139} height={110} className="mb-8" />
        <div className="bg-white text-center space-y-6 rounded-lg max-w-[600px] w-full shadow-sm p-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-baseBlue mx-auto"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center flex-col min-h-screen py-6 bg-gray-50">
      <Logo width={139} height={110} className="mb-8" />

      <div className="bg-white text-left space-y-6 rounded-lg max-w-[700px] w-full shadow-sm overflow-hidden">
        <div className="w-full h-12 rounded-t-lg overflow-hidden">
          <img
            src={GradientHeader}
            alt="Header gradient"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="px-10 pt-10 pb-8">
          <div className="text-center mb-6">
            {/* Cloud with sad expression and lightning bolt illustration */}
            <div className="flex justify-center mb-6">
              <Balloon width={120} height={120} />
            </div>

            <h1 className="font-bold text-2xl text-gray-800 mb-2">
              Last Day! Keep Your Echo Ratings Access Active
            </h1>
          </div>

          <div className="space-y-4">
            <p className="text-gray-800">Hi {firstName},</p>

            <p className="text-gray-800">
              Today is the final day of your 14-day free trial! Don't lose
              access to the premium features that help you make smarter business
              decisions.
            </p>

            <p className="text-gray-800">
              To continue accessing all the powerful tools you love, upgrade to
              the Premium Plan today and ensure uninterrupted service.
            </p>
          </div>

          <div className="text-center pt-6 flex gap-2 flex-col">
            <Link
              to="/trial-welcome"
              className="inline-block w-full bg-brand-baseBlue text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              Upgrade Now
            </Link>
            <Link
              to="/"
              className="w-full bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-lg flex items-center justify-center gap-2"
            >
              Go to Dashboard
            </Link>
          </div>

          <div className="pt-6 bg-[#F1F9FF] p-4 rounded-lg mt-8 mb-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              Upgrade now to continue enjoying:
            </h3>
            <div className="space-y-2">
              {[
                'Campaigns',
                'Automations',
                'Member seats',
                'Leading review platforms',
                'Bulk and manual import customers',
                'Analytics',
                'AI Assisted Reviews for stronger feedback',
                'AI Assisted email follow up and customization',
                'Industry-leading support',
                'Ongoing support',
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <ResolvedIcon width={16} height={16} />
                  <span className="text-gray-800 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center pt-4">
            <p className="text-gray-600 text-sm">
              Have any questions? Our support team is here to help at{' '}
              <a
                href="mailto:contact@echoratings.com"
                className="text-brand-baseBlue underline"
              >
                contact@echoratings.com
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10 text-center text-gray-500 text-sm space-y-1">
        <p>Echo Ratings</p>
        <p>©{new Date().getFullYear()} Echo Ratings</p>
      </div>
    </div>
  );
};
